import { useState } from "react";
import { isNativeBuild } from "@/app/apiBase";
import { useApp } from "@/app/container";
import { useQuery } from "@/app/hooks/useQuery";
import { useLocale } from "@/app/i18n/LocaleProvider";
import { format } from "@/app/i18n/strings";
import type { ProviderId } from "@/services/ApiClient";
import { Button } from "../Button";
import styles from "./account.module.css";
import { describeError } from "./errors";
import settingsStyles from "@/app/screens/SettingsScreen.module.css";

/** Settings section shown while signed out: an email code and the domain's identity providers. */
export function SignInSection({ authError }: { authError: string | null }) {
  const app = useApp();
  const { t, locale } = useLocale();
  const providers = useQuery<ProviderId[]>(() => app.account.providers(), []);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState<"email" | "code">("email");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(authError);

  const attempt = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (cause) {
      setError(describeError(cause, t));
    } finally {
      setBusy(false);
    }
  };
  const sendCode = () => attempt(async () => {
    await app.account.startEmail(email, locale);
    setStage("code");
    setCode("");
  });
  const verify = () => attempt(async () => {
    await app.account.verifyEmail(email, code);
    app.analytics.track("sign_in", { provider: "email" });
  });

  const list = providers.data ?? [];
  // Identity providers redirect through the site's own /api origin, which the native app does not have.
  const oauth = isNativeBuild() ? [] : list.filter((p): p is Exclude<ProviderId, "email"> => p !== "email");

  return (
    <section className={settingsStyles.section}>
      <h2 className={settingsStyles.heading}>{t.account.signInTitle}</h2>
      <div className={styles.card}>
        <p className={styles.muted}>{t.account.signInHint}</p>
        {list.includes("email") && stage === "email" && (
          <form className={styles.form} onSubmit={(e) => { e.preventDefault(); void sendCode(); }}>
            <label className={styles.field}>
              {t.account.emailLabel}
              <input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </label>
            <div className={styles.actions}>
              <Button type="submit" variant="primary" disabled={busy || email.trim() === ""}>{t.account.sendCode}</Button>
            </div>
          </form>
        )}
        {list.includes("email") && stage === "code" && (
          <form className={styles.form} onSubmit={(e) => { e.preventDefault(); void verify(); }}>
            <p className={styles.muted}>{format(t.account.codeSentTo, { email: email.trim() })}</p>
            <label className={styles.field}>
              {t.account.codeLabel}
              <input inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} required />
            </label>
            <div className={styles.actions}>
              <Button type="submit" variant="primary" disabled={busy || code.length !== 6}>{t.account.signIn}</Button>
              <Button variant="ghost" disabled={busy} onClick={() => void sendCode()}>{t.account.resendCode}</Button>
              <Button variant="ghost" disabled={busy} onClick={() => { setStage("email"); setError(null); }}>{t.account.changeEmail}</Button>
            </div>
          </form>
        )}
        {oauth.length > 0 && (
          <div className={styles.actions}>
            {oauth.map((provider) => (
              <a key={provider} href={`/api/auth/${provider}/start`}>
                <Button tabIndex={-1}>{format(t.account.continueWith, { provider: t.account.providers[provider] })}</Button>
              </a>
            ))}
          </div>
        )}
        {error && <p className={styles.error} role="alert">{error}</p>}
      </div>
    </section>
  );
}
